import React from 'react';
import { BrowserRouter as Router, Navigate, Routes, Route } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { AuthProvider } from './contexts/AuthContext';
import Home from './pages/Home';
import ImpactPage from './pages/ImpactPage';

import ContactPage from './pages/ContactPage';
import PlatformPage from './pages/PlatformPage';
import FieldIntelligencePage from './pages/FieldIntelligencePage';
import TraceabilityPage from './pages/TraceabilityPage';
import Blog from './pages/Blog';
import BlogPostPage from './pages/BlogPostPage';
import Marketplace from './pages/Marketplace';
import TryEsanzaPage from './pages/TryEsanzaPage';
import PublicTraceCertificatePage from './pages/PublicTraceCertificatePage';
import PrivacyPolicyPage from './pages/PrivacyPolicyPage';
import DataDeletionPage from './pages/DataDeletionPage';
import GalleryPage from './pages/GalleryPage';
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import OrderHistoryPage from './pages/OrderHistoryPage';
import AuthCallbackPage from './pages/AuthCallbackPage';
import ProtectedRoute from './components/ProtectedRoute';
import BuyerOverviewPage from './pages/buyer/BuyerOverviewPage';
import BuyerOrdersPage from './pages/buyer/BuyerOrdersPage';
import BuyerOrderDetailPage from './pages/buyer/BuyerOrderDetailPage';
import BuyerMarketplacePage from './pages/buyer/BuyerMarketplacePage';
import BuyerRfqsPage from './pages/buyer/BuyerRfqsPage';
import BuyerRfqCreatePage from './pages/buyer/BuyerRfqCreatePage';
import BuyerRfqDetailPage from './pages/buyer/BuyerRfqDetailPage';
import BuyerQuotationsPage from './pages/buyer/BuyerQuotationsPage';
import BuyerQuotationDetailPage from './pages/buyer/BuyerQuotationDetailPage';
import BuyerDeliveriesPage from './pages/buyer/BuyerDeliveriesPage';
import BuyerInvoicesPage from './pages/buyer/BuyerInvoicesPage';
import BuyerSuppliersPage from './pages/buyer/BuyerSuppliersPage';
import BuyerOrganisationPage from './pages/buyer/BuyerOrganisationPage';
import BuyerProfilePage from './pages/buyer/BuyerProfilePage';
import './App.css';

import AboutPage from './pages/AboutPage';
import { featureFlags } from './config/esanzaContent';
import { useWebsiteContent } from './hooks/useWebsiteContent';

const PublicPageRoute: React.FC<{ pageKey: string; children: React.ReactElement }> = ({ pageKey, children }) => {
  const { page, loading } = useWebsiteContent(pageKey);
  if (loading) return children;
  if (page && page.visible === false) return <Navigate to="/" replace />;
  return children;
};

const MarketplaceRoute: React.FC = () => {
  const { settings, page, loading } = useWebsiteContent('marketplace');
  const marketplaceVisible = Boolean(settings.marketplace_visible ?? featureFlags.marketplaceVisible);
  if (loading) return <Marketplace />;
  if (!marketplaceVisible || page?.visible === false) return <Navigate to="/platform" replace />;
  return <Marketplace />;
};

const App = () => {
  return (
    <HelmetProvider>
      <AuthProvider>
        <Router>
          <Routes>
            <Route path="/" element={<Home />} />
              <Route path="/about" element={<PublicPageRoute pageKey="about"><AboutPage /></PublicPageRoute>} />
              <Route path="/platform" element={<PublicPageRoute pageKey="platform"><PlatformPage /></PublicPageRoute>} />
              <Route path="/field-intelligence" element={<FieldIntelligencePage />} />
              <Route path="/ai" element={<Navigate to="/field-intelligence" replace />} />
              <Route path="/traceability" element={<PublicPageRoute pageKey="traceability"><TraceabilityPage /></PublicPageRoute>} />
              <Route path="/trace/:code" element={<PublicTraceCertificatePage />} />
              <Route path="/try-esanza" element={<TryEsanzaPage />} />
              <Route path="/privacy" element={<PrivacyPolicyPage />} />
              <Route path="/delete-account" element={<DataDeletionPage />} />

              {/* Auth routes */}
              <Route path="/login" element={<LoginPage />} />
              <Route path="/register" element={<RegisterPage />} />
              <Route path="/auth/callback" element={<AuthCallbackPage />} />

              {/* Protected buyer routes */}
              <Route path="/orders" element={<ProtectedRoute><OrderHistoryPage /></ProtectedRoute>} />
              <Route path="/dashboard/orders" element={<Navigate to="/orders" replace />} />

              {/* Buyer Portal */}
              <Route path="/account" element={<ProtectedRoute><BuyerOverviewPage /></ProtectedRoute>} />
              <Route path="/account/orders" element={<ProtectedRoute><BuyerOrdersPage /></ProtectedRoute>} />
              <Route path="/account/orders/:id" element={<ProtectedRoute><BuyerOrderDetailPage /></ProtectedRoute>} />
              <Route path="/account/marketplace" element={<ProtectedRoute><BuyerMarketplacePage /></ProtectedRoute>} />
              <Route path="/account/rfqs" element={<ProtectedRoute><BuyerRfqsPage /></ProtectedRoute>} />
              <Route path="/account/rfqs/new" element={<ProtectedRoute><BuyerRfqCreatePage /></ProtectedRoute>} />
              <Route path="/account/rfqs/:id" element={<ProtectedRoute><BuyerRfqDetailPage /></ProtectedRoute>} />
              <Route path="/account/quotations" element={<ProtectedRoute><BuyerQuotationsPage /></ProtectedRoute>} />
              <Route path="/account/quotations/:id" element={<ProtectedRoute><BuyerQuotationDetailPage /></ProtectedRoute>} />
              <Route path="/account/deliveries" element={<ProtectedRoute><BuyerDeliveriesPage /></ProtectedRoute>} />
              <Route path="/account/invoices" element={<ProtectedRoute><BuyerInvoicesPage /></ProtectedRoute>} />
              <Route path="/account/suppliers" element={<ProtectedRoute><BuyerSuppliersPage /></ProtectedRoute>} />
              <Route path="/account/organisation" element={<ProtectedRoute><BuyerOrganisationPage /></ProtectedRoute>} />
              <Route path="/account/profile" element={<ProtectedRoute><BuyerProfilePage /></ProtectedRoute>} />

              {/* Marketplace */}
              <Route path="/buy" element={<MarketplaceRoute />} />
              <Route path="/marketplace" element={<MarketplaceRoute />} />
              <Route path="/model" element={<Navigate to="/platform" replace />} />
              <Route path="/impact" element={<PublicPageRoute pageKey="impact"><ImpactPage /></PublicPageRoute>} />
              <Route path="/blog" element={<PublicPageRoute pageKey="blog"><Blog /></PublicPageRoute>} />
              <Route path="/blog/:slug" element={<BlogPostPage />} />
              <Route path="/contact" element={<PublicPageRoute pageKey="contact"><ContactPage /></PublicPageRoute>} />
              <Route path="/gallery" element={<GalleryPage />} />
          </Routes>
        </Router>
      </AuthProvider>
    </HelmetProvider>
  );
};

export default App;
